import type React from "react";
import { useState } from "react";
import Dropzone from "react-dropzone";

// The sole owner of react-dropzone. Every file-selection surface in the
// application renders this component; nothing else may import the library.
// Enforced by components/primitives/importBoundary.test.ts.
//
// This component SELECTS files. It never uploads them, never reads them, and
// never issues a request. Callers receive the selected File objects and decide
// what to do with them. Preview and download are application concerns and live
// in the adapters above this module.
//
// Three things this boundary deliberately owns rather than inherits from the
// library, because all three changed across the majors between 12 and 19:
//
//  1. `accept` is the object shape (MIME type -> extensions). react-dropzone 12
//     took an array; 13 changed it. Call sites use one shape regardless.
//  2. The file input's accessible name. From 19 the library injects
//     `aria-label="file upload"` into getInputProps, ahead of the caller's own
//     props, which silently outranks an associated <label>. This module always
//     passes an explicit value — the caller's, or undefined — so the name is
//     never decided by the library.
//  3. The file input's hidden geometry. The library's inline style is not part
//     of its API and changed in 19 in a way that interacts badly with this
//     application's own `.app-input-group-file input[type=file]` overlay rule.
//     Pinned here so the geometry cannot move underneath us again.

/**
 * MIME type -> permitted file extensions. `[]` means "any file of this type".
 * Deliberately not exported: every call site is untyped JSX, and Knip fails on
 * an export nothing consumes.
 */
type FileDropzoneAccept = Record<string, readonly string[]>;

type FileDropzoneProps = {
  accept?: FileDropzoneAccept;
  "aria-describedby"?: string;
  "aria-invalid"?: true;
  /**
   * Accessible name for the file input. Provide this only when there is no
   * `label`; the two are alternative ways to name the same control.
   */
  "aria-label"?: string;
  "aria-required"?: true;
  /** Contents of the button-styled trigger. */
  buttonLabel?: React.ReactNode;
  /** Rendered after the control, inside `className`'s wrapper. */
  children?: React.ReactNode;
  /** Wrapper class. Omit to render the control with no wrapper at all. */
  className?: string;
  disabled?: boolean;
  id?: string;
  label?: React.ReactNode;
  maxFiles?: number;
  multiple?: boolean;
  /** Called with the accepted files whenever a selection is made. */
  onSelect: (files: File[]) => void;
  /** Selected files, or a stored server-side path. Only files are displayed. */
  value?: readonly File[] | string | null;
};

/**
 * The library's own hidden-input style is an implementation detail, and in 19 it
 * stopped being `position: absolute`. This application's legacy stylesheet
 * (`public/css/global/style.css`) styles the same input as a full-size
 * `position: absolute` overlay with `min-width: 100%`, while
 * `.app-input-group > .app-form-control` — a class the control also carries —
 * asks for `position: relative; float: left; display: table-cell`. Without an
 * inline `position` the second rule wins on specificity and a 100%-wide floated
 * box lands in the flow of a `display: table` input group.
 *
 * Pinning the pre-upgrade inline style keeps the control out of flow and keeps
 * this wave a geometry no-op. Asserted in the browser by
 * `e2e/cross-browser-smoke.spec.js`.
 */
const HIDDEN_FILE_INPUT_STYLE: React.CSSProperties = {
  border: 0,
  clip: "rect(0, 0, 0, 0)",
  clipPath: "inset(50%)",
  height: "1px",
  margin: "0 -1px -1px 0",
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
};

/** A stored path renders nothing here; only a live selection is displayed. */
function selectedFileNames(value: readonly File[] | string | null | undefined): string {
  return Array.isArray(value) ? value.map((file) => file.name).join(", ") : "";
}

export default function FileDropzone({
  accept,
  buttonLabel = "Upload",
  children,
  className,
  disabled,
  id,
  label,
  maxFiles = 0,
  multiple = false,
  onSelect,
  value,
  ...aria
}: FileDropzoneProps): React.ReactElement {
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const control = (
    <Dropzone
      accept={accept}
      disabled={disabled}
      maxFiles={maxFiles}
      multiple={multiple}
      noClick={disabled}
      noKeyboard={disabled}
      onDrop={(acceptedFiles: File[], fileRejections) => {
        if (disabled) {
          return;
        }
        if (fileRejections.length > 0) {
          const tooMany = fileRejections.some((rejection) =>
            rejection.errors.some((error) => error.code === "too-many-files"),
          );
          setSelectionError(
            tooMany
              ? multiple && maxFiles > 0
                ? `Select at most ${maxFiles} ${maxFiles === 1 ? "file" : "files"} at a time.`
                : "Select one file at a time."
              : "The selected file type is not accepted.",
          );
          onSelect([]);
          return;
        }
        setSelectionError(null);
        onSelect(acceptedFiles);
      }}
    >
      {({ getInputProps, getRootProps }) => (
        <>
          {label !== undefined && (
            <label className="app-form-label" htmlFor={id}>
              {label}
            </label>
          )}
          <div
            className="app-input-group app-input-group-file"
            // The root coordinates react-dropzone's click and drop handlers but
            // is not itself interactive. A real button below owns activation,
            // avoiding a button role that contains nested form controls.
            {...getRootProps({ role: "group", tabIndex: -1 })}
          >
            <span className={`fileUpload app-btn app-btn--info${disabled ? " disabled" : ""}`}>
              <button
                disabled={disabled}
                style={{
                  appearance: "none",
                  background: "transparent",
                  border: 0,
                  color: "inherit",
                  cursor: "inherit",
                  font: "inherit",
                  height: "100%",
                  padding: 0,
                  width: "100%",
                }}
                type="button"
              >
                {buttonLabel}
              </button>
            </span>
            <input
              aria-hidden="true"
              className="app-form-control"
              disabled={disabled}
              readOnly
              tabIndex={-1}
              type="text"
              value={selectedFileNames(value)}
            />
            <input
              {...getInputProps({
                "aria-describedby": aria["aria-describedby"],
                "aria-invalid": aria["aria-invalid"],
                // Always explicit, so the library cannot name this control.
                "aria-label": aria["aria-label"],
                "aria-required": aria["aria-required"],
                id,
                style: HIDDEN_FILE_INPUT_STYLE,
              })}
              className="app-form-control"
              disabled={disabled}
              readOnly
            />
          </div>
          {selectionError && (
            <div className="app-form-help with-errors" role="alert">
              {selectionError}
            </div>
          )}
        </>
      )}
    </Dropzone>
  );

  if (className === undefined) {
    return control;
  }

  // The nested div is load-bearing: `.dropzone-wrapper > div` in
  // styles/theme/_base.scss supplies the pointer cursor across the whole
  // control, including its label.
  return (
    <div className={className}>
      <div>{control}</div>
      {children}
    </div>
  );
}
